import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  fetchCourseProgress,
  saveCourseResponse,
  markChecklistItem,
} from '../services/creatorDashboard';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

interface CreatorCourseProps {
  talentProfile: { id: string };
  onBack: () => void;
}


const REWARD_PER_QUESTION = 5;
const TOTAL_REWARD = 30;
const TOTAL_QUESTIONS = 6;
const COURSE_DEADLINE_HOURS = 24;

interface QuestionDef {
  number: number;
  preText?: React.ReactNode;
  question: string;
  options: string[];
  allowOther?: boolean;
  conditionalFollowUp?: {
    triggerAnswer: string;
    followUpText: string;
    followUpOptions: string[];
  };
}

const QUESTIONS: QuestionDef[] = [
  {
    number: 1,
    question: "What's your primary platform?",
    options: ['Instagram', 'YouTube', 'Rumble', 'X', 'TikTok', 'Facebook'],
  },
  {
    number: 2,
    preText: (
      <div className="space-y-4 text-gray-300 text-sm leading-relaxed">
        <p>
          Instagram (and most platforms) let you add multiple links in your bio. This is primarily to
          discourage people from leaving their platform by presenting a bad link experience.
        </p>
        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
          <p className="font-bold text-white text-base">
            By adding a single link in bio, you're driving an average of 61% more people to your links.
          </p>
        </div>
        <p>
          We want to help you maximize views, because more views = higher click through rate (CTR) which
          increases what you can earn per click on pre-paid sponsors.
        </p>
      </div>
    ),
    question: 'Did you use your ShoutOut Fans link exclusively on all your platforms?',
    options: ['Yes', 'No'],
  },
  {
    number: 3,
    preText: (
      <p className="text-gray-300 text-sm leading-relaxed">
        Followers don't know what you don't tell them. Which ShoutOut feature would motivate your
        followers the most to click your link in bio?
      </p>
    ),
    question: 'Which feature would motivate your followers the most?',
    options: [
      'Personalized ShoutOut Videos',
      'Messaging / Donating',
      'Joining your mailing list',
      'Other links (like petitions, merch?)',
    ],
  },
  {
    number: 4,
    question: 'Do you sell merch?',
    options: ['Yes', 'No'],
    conditionalFollowUp: {
      triggerAnswer: 'No',
      followUpText: 'Would you be interested in earning from merch if it was easy to add products without needing to manage inventory?',
      followUpOptions: ['Yes', 'No'],
    },
  },
  {
    number: 5,
    preText: (
      <p className="text-gray-300 text-sm leading-relaxed">
        Our passion is to help you grow and get your message out to more people. We can help you do
        that by connecting you to more creators like you so you can collab and network.
      </p>
    ),
    question: 'Have you talked to anyone in the chat yet?',
    options: ['Yes', 'No'],
  },
  {
    number: 6,
    preText: (
      <p className="text-gray-300 text-sm leading-relaxed">
        ShoutOut finds pre-paid sponsors for you, letting them add small cards to your link.
        We currently limit 2 per page.
      </p>
    ),
    question: 'Would you be interested in allowing more sponsors on your link to earn more, or is 2 plenty?',
    options: ['2 is enough', 'Fine with more'],
  },
];

const STORAGE_KEY_PREFIX = 'course_started_';

function getCourseDeadline(talentId: string): Date | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + talentId);
    if (!stored) return null;
    return new Date(stored);
  } catch { return null; }
}

function setCourseDeadline(talentId: string) {
  const deadline = new Date(Date.now() + COURSE_DEADLINE_HOURS * 60 * 60 * 1000);
  localStorage.setItem(STORAGE_KEY_PREFIX + talentId, deadline.toISOString());
  return deadline;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00:00';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const AcademicCapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
  </svg>
);

const CreatorCourse: React.FC<CreatorCourseProps> = ({ talentProfile, onBack }) => {
  const [currentPage, setCurrentPage] = useState<'intro' | number | 'end'>('intro');
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [followUpAnswers, setFollowUpAnswers] = useState<Record<number, string>>({});
  const [earnedSoFar, setEarnedSoFar] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExitModal, setShowExitModal] = useState(false);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const tickRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // Animation states
  const [slideDir, setSlideDir] = useState<'in' | 'out'>('in');
  const [rewardPulse, setRewardPulse] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [earnBadgeAmount, setEarnBadgeAmount] = useState(0);

  useEffect(() => {
    const existing = getCourseDeadline(talentProfile.id);
    if (existing) {
      setDeadline(existing);
      if (existing.getTime() <= Date.now()) setIsExpired(true);
    }
  }, [talentProfile.id]);

  useEffect(() => {
    if (!deadline) return;
    const tick = () => {
      const remaining = deadline.getTime() - Date.now();
      if (remaining <= 0) {
        setCountdown('0:00:00');
        setIsExpired(true);
        if (tickRef.current) clearInterval(tickRef.current);
      } else {
        setCountdown(formatCountdown(remaining));
      }
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [deadline]);

  const loadProgress = useCallback(async () => {
    try {
      const progress = await fetchCourseProgress(talentProfile.id);
      const loaded: Record<number, string> = {};
      const loadedFollowUp: Record<number, string> = {};
      Object.entries(progress).forEach(([qn, answer]) => {
        const num = parseInt(qn);
        if (num > 100) loadedFollowUp[num - 100] = answer;
        else loaded[num] = answer;
      });
      setAnswers(loaded);
      setFollowUpAnswers(loadedFollowUp);
      setEarnedSoFar(Object.keys(loaded).length * REWARD_PER_QUESTION);

      if (Object.keys(loaded).length >= TOTAL_QUESTIONS) {
        setCurrentPage('end');
      } else if (Object.keys(loaded).length > 0) {
        const maxAnswered = Math.max(...Object.keys(loaded).map(Number));
        setCurrentPage(maxAnswered < TOTAL_QUESTIONS ? maxAnswered + 1 : 'end');
      }
    } catch (error) {
      console.error('Error loading course progress:', error);
    } finally {
      setLoading(false);
    }
  }, [talentProfile.id]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const answeredCount = Object.keys(answers).length;
  const isComplete = answeredCount >= TOTAL_QUESTIONS;

  const animateTransition = (nextPage: 'end' | number) => {
    setSlideDir('out');
    setTimeout(() => {
      if (nextPage === 'end') handleCourseComplete();
      else setCurrentPage(nextPage);
      setSlideDir('in');
    }, 300);
  };

  const handleAnswer = async (questionNumber: number, answer: string) => {
    if (saving) return;
    setSelectedOption(answer);
    setSaving(true);

    try {
      await saveCourseResponse(talentProfile.id, questionNumber, answer);
      setAnswers(prev => ({ ...prev, [questionNumber]: answer }));

      const newAnswered = Object.keys({ ...answers, [questionNumber]: answer }).length;
      const newEarned = newAnswered * REWARD_PER_QUESTION;

      if (!deadline) setDeadline(setCourseDeadline(talentProfile.id));

      if (questionNumber === 6 && answer === 'Fine with more') {
        supabase
          .from('talent_profiles')
          .update({ max_sponsor_ads: 3 })
          .eq('id', talentProfile.id)
          .then(({ error }) => {
            if (error) console.error('Error updating sponsor limit:', error);
          });
      }

      // Trigger reward animation after a short delay for the selection to register
      setTimeout(() => {
        setEarnBadgeAmount(newEarned);
        setRewardPulse(true);
        setShowConfetti(true);
        setEarnedSoFar(newEarned);
      }, 400);

      // End animations and transition to next
      setTimeout(() => {
        setRewardPulse(false);
        setShowConfetti(false);
        setSelectedOption(null);
      }, 1800);

      const question = QUESTIONS.find(q => q.number === questionNumber);
      if (question?.conditionalFollowUp && answer === question.conditionalFollowUp.triggerAnswer) {
        setSaving(false);
        return;
      }

      setTimeout(() => {
        const nextPage = questionNumber >= TOTAL_QUESTIONS ? 'end' as const : questionNumber + 1;
        animateTransition(nextPage);
      }, 1800);
    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error('Failed to save answer');
      setSelectedOption(null);
    } finally {
      setSaving(false);
    }
  };

  const handleFollowUpAnswer = async (questionNumber: number, answer: string) => {
    if (saving) return;
    setSelectedOption(answer);
    setSaving(true);

    try {
      await saveCourseResponse(talentProfile.id, questionNumber + 100, answer);
      setFollowUpAnswers(prev => ({ ...prev, [questionNumber]: answer }));

      setTimeout(() => {
        setSelectedOption(null);
        const nextPage = questionNumber >= TOTAL_QUESTIONS ? 'end' as const : questionNumber + 1;
        animateTransition(nextPage);
      }, 600);
    } catch (error) {
      console.error('Error saving follow-up:', error);
      toast.error('Failed to save answer');
      setSelectedOption(null);
    } finally {
      setSaving(false);
    }
  };

  const handleCourseComplete = async () => {
    setCurrentPage('end');
    try {
      await markChecklistItem(talentProfile.id, 'complete_course');
      await supabase
        .from('talent_profiles')
        .update({ course_completed: true, course_payout_cents: TOTAL_REWARD * 100 })
        .eq('id', talentProfile.id);
    } catch (error) {
      console.error('Error completing course:', error);
    }
  };

  const handleExitAttempt = () => {
    if (isComplete || currentPage === 'end' || currentPage === 'intro') {
      onBack();
      return;
    }
    setShowExitModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto relative">
      {/* Confetti overlay */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                left: `${30 + Math.random() * 40}%`,
                top: '-4px',
                backgroundColor: ['#22c55e', '#3b82f6', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'][i % 6],
                animation: `confetti-fall ${1.2 + Math.random() * 0.8}s ease-out forwards`,
                animationDelay: `${Math.random() * 0.3}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Floating +$5 badge */}
      {rewardPulse && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div
            className="text-3xl font-black text-green-400 drop-shadow-[0_0_20px_rgba(34,197,94,0.6)]"
            style={{ animation: 'float-up 1.4s ease-out forwards' }}
          >
            +${REWARD_PER_QUESTION}
          </div>
        </div>
      )}

      {/* Progress header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleExitAttempt}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3">
            {deadline && !isComplete && (
              <div className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full ${
                isExpired ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-gray-400'
              }`}>
                <ClockIcon className="h-3.5 w-3.5" />
                {isExpired ? 'Expired' : countdown}
              </div>
            )}
            {typeof currentPage === 'number' && (
              <span className="text-sm text-gray-500">
                {currentPage}/{TOTAL_QUESTIONS}
              </span>
            )}
          </div>
        </div>

        {/* Step dots with smooth fill */}
        <div className="flex gap-1.5">
          {QUESTIONS.map((q) => (
            <div
              key={q.number}
              className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden"
            >
              <div
                className={`h-full rounded-full transition-all duration-700 ease-out ${
                  answers[q.number] ? 'w-full bg-green-500' : currentPage === q.number ? 'w-1/2 bg-blue-500' : 'w-0'
                }`}
              />
            </div>
          ))}
        </div>

        {/* Earnings display */}
        <div className="flex items-center justify-center mt-5">
          <div className={`relative flex items-center gap-2.5 px-5 py-2.5 rounded-2xl border transition-all duration-700 ${
            rewardPulse
              ? 'bg-green-500/20 border-green-500/40 scale-110 shadow-[0_0_30px_rgba(34,197,94,0.3)]'
              : 'bg-white/5 border-white/10'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
              rewardPulse ? 'bg-green-500 scale-110' : 'bg-white/10'
            }`}>
              <CurrencyDollarIcon className={`h-5 w-5 transition-colors duration-500 ${
                rewardPulse ? 'text-white' : 'text-green-400'
              }`} />
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className={`font-bold text-xl tabular-nums transition-all duration-500 ${
                rewardPulse ? 'text-green-400' : 'text-white'
              }`}>
                ${earnedSoFar}
              </span>
              <span className="text-gray-500 text-sm">/ ${TOTAL_REWARD}</span>
            </div>
          </div>
        </div>

        {!isComplete && answeredCount > 0 && !rewardPulse && (
          <p className="text-center text-xs text-gray-600 mt-2">
            Paid after all {TOTAL_QUESTIONS} questions
          </p>
        )}
      </div>

      {/* Intro Page */}
      {currentPage === 'intro' && (
        <div
          className="border border-white/10 rounded-2xl bg-gradient-to-b from-white/[0.06] to-transparent p-8 text-center space-y-6"
          style={{ animation: 'fade-slide-up 0.4s ease-out' }}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-blue-500/20">
            <AcademicCapIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Earn $30 by answering a few questions</h1>
          <p className="text-gray-400 leading-relaxed max-w-lg mx-auto text-sm">
            We've spent over 10 years marketing for over 500 influencers and businesses across America
            which drives our primary mission: help free-speech creators monetize their audiences and
            break the chains of demonetization culture.
          </p>
          <button
            onClick={() => { setSlideDir('out'); setTimeout(() => { setCurrentPage(1); setSlideDir('in'); }, 200); }}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            Let's go
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Question Pages */}
      {typeof currentPage === 'number' && (() => {
        const question = QUESTIONS.find(q => q.number === currentPage);
        if (!question) return null;

        const currentAnswer = answers[question.number];
        const showFollowUp = question.conditionalFollowUp
          && currentAnswer === question.conditionalFollowUp.triggerAnswer
          && !followUpAnswers[question.number];

        return (
          <div
            className={`border border-white/10 rounded-2xl bg-gradient-to-b from-white/[0.06] to-transparent p-6 sm:p-8 space-y-6 transition-all duration-300 ${
              slideDir === 'out' ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'
            }`}
            style={slideDir === 'in' ? { animation: 'fade-slide-up 0.35s ease-out' } : undefined}
          >
            {question.preText && <div className="animate-[fade-in_0.3s_ease-out]">{question.preText}</div>}
            <h2 className="text-lg sm:text-xl font-bold text-white leading-snug">{question.question}</h2>

            <div className="grid gap-2.5">
              {question.options.map((option, idx) => {
                const isSelected = currentAnswer === option;
                const isJustPicked = selectedOption === option;
                const isDisabled = saving || (!!currentAnswer && !showFollowUp);
                return (
                  <button
                    key={option}
                    onClick={() => handleAnswer(question.number, option)}
                    disabled={isDisabled}
                    className={`relative flex items-center gap-3 px-5 py-4 rounded-xl border text-left transition-all duration-300 overflow-hidden ${
                      isJustPicked
                        ? 'border-green-500 bg-green-500/15 text-white scale-[1.02] shadow-lg shadow-green-500/10'
                        : isSelected
                        ? 'border-green-500/50 bg-green-500/10 text-white'
                        : 'border-white/10 text-gray-300 hover:border-white/25 hover:bg-white/[0.04]'
                    } ${isDisabled && !isSelected ? 'opacity-40' : ''}`}
                    style={{ animationDelay: `${idx * 50}ms`, animation: slideDir === 'in' ? `fade-slide-up 0.35s ease-out ${idx * 50}ms both` : undefined }}
                  >
                    {/* Selection ripple */}
                    {isJustPicked && (
                      <div
                        className="absolute inset-0 bg-green-500/10 rounded-xl"
                        style={{ animation: 'ripple-out 0.6s ease-out forwards' }}
                      />
                    )}
                    <div className={`relative w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isSelected || isJustPicked ? 'border-green-500 bg-green-500 scale-110' : 'border-gray-600'
                    }`}>
                      {(isSelected || isJustPicked) && (
                        <CheckIcon className="h-3 w-3 text-white" style={{ animation: 'pop-in 0.25s ease-out' }} />
                      )}
                    </div>
                    <span className="font-medium relative">{option}</span>
                  </button>
                );
              })}
            </div>

            {/* Conditional Follow-up */}
            {showFollowUp && question.conditionalFollowUp && (
              <div className="mt-4 pt-5 border-t border-white/10 space-y-4" style={{ animation: 'fade-slide-up 0.35s ease-out' }}>
                <p className="text-gray-300 text-sm">{question.conditionalFollowUp.followUpText}</p>
                <div className="grid gap-2.5">
                  {question.conditionalFollowUp.followUpOptions.map((option) => {
                    const isSelected = followUpAnswers[question.number] === option;
                    return (
                      <button
                        key={option}
                        onClick={() => handleFollowUpAnswer(question.number, option)}
                        disabled={saving}
                        className={`flex items-center gap-3 px-5 py-4 rounded-xl border text-left transition-all duration-300 ${
                          isSelected
                            ? 'border-green-500 bg-green-500/15 text-white'
                            : 'border-white/10 text-gray-300 hover:border-white/25 hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                          isSelected ? 'border-green-500 bg-green-500' : 'border-gray-600'
                        }`}>
                          {isSelected && <CheckIcon className="h-3 w-3 text-white" style={{ animation: 'pop-in 0.25s ease-out' }} />}
                        </div>
                        <span className="font-medium">{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* End Page */}
      {currentPage === 'end' && (
        <div
          className="border border-white/10 rounded-2xl bg-gradient-to-b from-white/[0.06] to-transparent p-8 text-center space-y-6"
          style={{ animation: 'fade-slide-up 0.5s ease-out' }}
        >
          <div
            className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-green-500/30"
            style={{ animation: 'pop-in 0.5s ease-out 0.2s both' }}
          >
            <CheckCircleIcon className="h-10 w-10 text-white" />
          </div>
          <div style={{ animation: 'fade-slide-up 0.4s ease-out 0.3s both' }}>
            <h1 className="text-2xl font-bold text-white mb-2">Thank you!</h1>
            <p className="text-gray-400 max-w-md mx-auto text-sm">
              Thank you so much for your answers, this helps us improve the ShoutOut product!
            </p>
          </div>
          <div
            className="inline-flex items-center gap-2.5 px-6 py-3 bg-green-500/15 border border-green-500/30 rounded-2xl"
            style={{ animation: 'pop-in 0.4s ease-out 0.5s both' }}
          >
            <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
            <span className="text-2xl font-bold text-green-400">${TOTAL_REWARD}</span>
            <span className="text-green-300 font-medium">earned</span>
          </div>
          <button
            onClick={onBack}
            className="block w-full max-w-xs mx-auto px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl transition-all"
            style={{ animation: 'fade-slide-up 0.4s ease-out 0.6s both' }}
          >
            Back to Dashboard
          </button>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-gray-900 border border-white/15 rounded-2xl p-6 w-full max-w-sm text-center space-y-5"
            style={{ animation: 'pop-in 0.3s ease-out' }}
          >
            <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
              <ClockIcon className="h-7 w-7 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">You're almost there!</h3>
              <p className="text-gray-400 text-sm">
                Complete all {TOTAL_QUESTIONS} questions within 24 hours to earn your <strong className="text-green-400">${TOTAL_REWARD}</strong>.
                You've answered {answeredCount} of {TOTAL_QUESTIONS} so far.
              </p>
            </div>
            {deadline && !isExpired && (
              <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-500 mb-1">Time remaining</p>
                <p className="text-2xl font-mono font-bold text-amber-400">{countdown}</p>
              </div>
            )}
            {isExpired && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                <p className="text-sm text-red-400 font-medium">Your 24-hour window has expired</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowExitModal(false); onBack(); }}
                className="flex-1 px-4 py-2.5 bg-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/15 transition-colors text-sm"
              >
                Leave anyway
              </button>
              <button
                onClick={() => setShowExitModal(false)}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500 transition-colors text-sm"
              >
                Keep going
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fade-slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pop-in {
          0% { opacity: 0; transform: scale(0.5); }
          70% { transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes float-up {
          0% { opacity: 0; transform: translateY(20px) scale(0.8); }
          20% { opacity: 1; transform: translateY(0) scale(1.1); }
          50% { opacity: 1; transform: translateY(-30px) scale(1); }
          100% { opacity: 0; transform: translateY(-60px) scale(0.9); }
        }
        @keyframes ripple-out {
          0% { opacity: 1; transform: scale(0.8); }
          100% { opacity: 0; transform: scale(1.5); }
        }
        @keyframes confetti-fall {
          0% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateY(60vh) rotate(720deg) scale(0.3); }
        }
      `}</style>
    </div>
  );
};

export default CreatorCourse;
